import React, { useState } from 'react';
import { MapPin, Sprout, Trees } from 'lucide-react';

export const FarmPlantSelector = ({ farms = [], crops = [], trees = [], onSelect, t }) => {
    const [selectedFarmId, setSelectedFarmId] = useState("");
    const [selectedCropId, setSelectedCropId] = useState("");
    const [selectedTreeId, setSelectedTreeId] = useState("");

    const handleFarmSelect = (e) => {
        const farmId = e.target.value;
        setSelectedFarmId(farmId);
        onSelect('farm_id', farmId);
        
        // Reset crop and tree when a new farm is selected
        setSelectedCropId("");
        onSelect('crop_id', '');
        setSelectedTreeId("");
        onSelect('tree_id', '');
    };

    const handleCropSelect = (e) => {
        const cropId = e.target.value;
        setSelectedCropId(cropId);
        onSelect('crop_id', cropId);
        
        // Reset tree when a new crop is selected
        setSelectedTreeId("");
        onSelect('tree_id', '');
    };

    const handleTreeSelect = (e) => {
        const treeId = e.target.value;
        setSelectedTreeId(treeId);
        onSelect('tree_id', treeId);
    };

    // Filter crops to only those belonging to the selected farm
    const filteredCrops = selectedFarmId 
        ? crops.filter(c => String(c.farm_id) === String(selectedFarmId))
        : [];
        
    const selectedCropObj = crops.find(c => String(c.id) === String(selectedCropId));
    const isTreeCrop = selectedCropObj?.is_tree;
    
    // Filter trees to only those belonging to the selected crop
    const filteredTrees = selectedCropId
        ? trees.filter(t => String(t.crop_id) === String(selectedCropId))
        : [];

    return (
        <div className="flex flex-row items-center space-x-3 w-full max-w-xl">
            {/* Top Level: Farm Selector */}
            <div className="relative flex-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MapPin size={16} className="text-[#C47A2C] dark:text-[#C47A2C] opacity-70" />
                </div>
                <select 
                    value={selectedFarmId}
                    onChange={handleFarmSelect} 
                    className="w-full bg-white/50 dark:bg-slate-800 border border-[#1B263B]/20 dark:border-slate-700 text-[#1B263B] dark:text-gray-100 pl-9 pr-3 py-2 rounded-xl outline-none focus:border-[#C47A2C] transition-colors appearance-none text-sm font-medium shadow-sm hover:bg-white/80 dark:hover:bg-slate-700/80 cursor-pointer"
                >
                    <option value="">{t?.common?.selectFarm || "-- Select Farm --"}</option>
                    {farms.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                </select>
            </div>
            
            {/* Context Level: Crop Selector */}
            <div className="relative flex-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Sprout size={16} className={`opacity-70 transition-colors ${selectedFarmId ? 'text-[#6B8F71]' : 'text-gray-400'}`} />
                </div>
                <select 
                    value={selectedCropId}
                    disabled={!selectedFarmId}
                    onChange={handleCropSelect} 
                    className={`w-full border pl-9 pr-3 py-2 rounded-xl outline-none transition-colors appearance-none text-sm font-medium shadow-sm cursor-pointer ${
                        selectedFarmId 
                        ? "bg-white/50 dark:bg-slate-800 border-[#1B263B]/20 dark:border-slate-700 text-[#1B263B] dark:text-gray-100 focus:border-[#6B8F71] hover:bg-white/80 dark:hover:bg-slate-700/80" 
                        : "bg-gray-100 dark:bg-slate-900 border-gray-200 dark:border-slate-800 text-gray-400 dark:text-gray-600 cursor-not-allowed"
                    }`}
                >
                    <option value="">{t?.common?.selectCrop || "-- Select Crop --"}</option>
                    {filteredCrops.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
            </div>
            
            {/* Third Level: Tree Selector (Only if crop is a tree) */}
            {isTreeCrop && (
                <div className="relative flex-1 animate-fade-in-slide">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Trees size={16} className={`opacity-70 transition-colors ${selectedCropId ? 'text-[#527056]' : 'text-gray-400'}`} />
                    </div>
                    <select 
                        value={selectedTreeId}
                        disabled={!selectedCropId}
                        onChange={handleTreeSelect} 
                        className={`w-full border pl-9 pr-3 py-2 rounded-xl outline-none transition-colors appearance-none text-sm font-medium shadow-sm cursor-pointer bg-white/50 dark:bg-slate-800 border-[#1B263B]/20 dark:border-slate-700 text-[#1B263B] dark:text-gray-100 focus:border-[#527056] hover:bg-white/80 dark:hover:bg-slate-700/80`}
                    >
                        <option value="">{t?.common?.selectTree || "-- Select Tree --"}</option>
                        {filteredTrees.map(t => <option key={t.id} value={t.id}>{t.tree_name} ({t.tree_code})</option>)}
                    </select>
                </div>
            )}
        </div>
    );
};
